import { Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { pstxPaginationButtonClasses } from './pstx-pagination.classes';

@Component({
  selector: 'pstx-pagination',
  standalone: true,
  imports: [CommonModule, TranslateModule],
  template: `
    <div
      class="tw-flex tw-flex-col sm:tw-flex-row sm:tw-items-center sm:tw-justify-between tw-gap-3"
    >
      <!-- LEFT: result summary (e.g. "Showing 1–4 of 4 orders"). The bold figures sit inline
           mid-sentence, so the copy is one translate key with markup rendered via innerHTML
           (the established pattern for inline-bold translated strings). -->
      <p
        class="tw-text-sm tw-text-body tw-m-0"
        [innerHTML]="
          'mysec.paginationShowing'
            | translate: { start: rangeStart, end: rangeEnd, total: totalItems, itemLabel: itemLabel }
        "
      ></p>

      <!-- RIGHT: page controls — ALWAYS rendered (prev/next stay visible, disabled at
           the boundaries) so the control never collapses on a single page. -->
      <nav
        class="tw-flex tw-items-center tw-gap-1"
        [attr.aria-label]="'mysec.paginationNav' | translate"
      >
        <button
          type="button"
          [class]="arrowClasses"
          [disabled]="currentPage <= 1"
          (click)="goTo(currentPage - 1)"
          [attr.aria-label]="'mysec.previousPage' | translate"
        >
          &lsaquo;
        </button>

        <button
          *ngFor="let page of pages; trackBy: trackPage"
          type="button"
          [class]="buttonClasses(page)"
          [attr.aria-current]="page === currentPage ? 'page' : null"
          (click)="goTo(page)"
        >
          {{ page }}
        </button>

        <button
          type="button"
          [class]="arrowClasses"
          [disabled]="currentPage >= totalPages"
          (click)="goTo(currentPage + 1)"
          [attr.aria-label]="'mysec.nextPage' | translate"
        >
          &rsaquo;
        </button>
      </nav>
    </div>
  `,
})
export class PstxPaginationComponent {
  @Input() currentPage = 1;
  @Input() totalItems = 0;
  @Input() pageSize = 20;
  @Input() maxButtons = 5;
  /** Trailing noun in the summary line, e.g. "orders" → "Showing 1–4 of 4 orders". */
  @Input() itemLabel = 'orders';

  @Output() pageChange = new EventEmitter<number>();

  get totalPages(): number {
    return Math.max(1, Math.ceil(this.totalItems / this.pageSize));
  }

  get rangeStart(): number {
    if (this.totalItems === 0) {
      return 0;
    }
    return (this.currentPage - 1) * this.pageSize + 1;
  }

  get rangeEnd(): number {
    return Math.min(this.currentPage * this.pageSize, this.totalItems);
  }

  // A sliding window of up to `maxButtons` page numbers centred on the current
  // page, clamped to the valid [1, totalPages] range.
  get pages(): number[] {
    const total = this.totalPages;
    const count = Math.min(this.maxButtons, total);
    let start = this.currentPage - Math.floor(count / 2);
    start = Math.max(1, Math.min(start, total - count + 1));
    return Array.from({ length: count }, (_, i) => start + i);
  }

  get arrowClasses(): string {
    return pstxPaginationButtonClasses({ active: false });
  }

  /** trackBy for the page-number *ngFor. pages() is a getter, so hardening it here
   *  costs nothing — the window is at most maxButtons numbers, cheap to diff either
   *  way (unlike FRC-1016's donut, which returned new objects, not primitives). */
  trackPage(_index: number, page: number): number {
    return page;
  }

  buttonClasses(page: number): string {
    return pstxPaginationButtonClasses({ active: page === this.currentPage });
  }

  goTo(page: number): void {
    const target = Math.max(1, Math.min(page, this.totalPages));
    if (target === this.currentPage) {
      return;
    }
    this.pageChange.emit(target);
  }
}
